import { BeatResolution, GenesisSampleType, SampleCategories } from '@arpeggi/shared'

import {
  ISampleWithEffectsStore,
  SampleWithEffectsStore
} from '../stores/song/sample/SampleWithEffectsStore'
import { InstrumentPart, Octave } from './genesis/genesis_proto_pb'

export class Arp2Utils {
  /** Creates a SampleWithEffects, setting the correct settings based on the category (e.g. Autorelease if a bass sample).  */
  static createSampleWithEffects(sampleIndex: number, category: string): ISampleWithEffectsStore {
    const sampleWithEffects = SampleWithEffectsStore.create()
    sampleWithEffects.updateSample(sampleIndex)
    const autoRelease = SampleCategories.Kick.includes(category)
    sampleWithEffects.updateAutoRelease(autoRelease)
    return sampleWithEffects
  }

  static getLengthOfRes(resolution: BeatResolution): number {
    switch (resolution) {
      case BeatResolution.QUARTER_NOTE:
        return 16
      case BeatResolution.EIGHTH_NOTE:
        return 32
      case BeatResolution.SIXTEENTH_NOTE:
        return 64

      case BeatResolution.QUARTER_NOTE_TRIPLETS:
        return 24
      case BeatResolution.EIGHTH_NOTE_TRIPLETS:
        return 48
      case BeatResolution.SIXTEENTH_NOTE_TRIPLETS:
        return 96
    }
  }

  static convertGenesisSampleWithEffectsToV2(
    type: GenesisSampleType,
    sample: number
  ): ISampleWithEffectsStore {
    let sampleId = this.genesisSampleToV2(type, sample)
    // If sample is -1, that means this is a sample that is no longer
    // available in v2. We'll simply ignore this stem and not add it to the stem database.
    return SampleWithEffectsStore.create({
      sampleIndex: sampleId,
      autoRelease: type === GenesisSampleType.BASS
    })
  }

  // Coordinate is a negative or positive value, with 0 being C(0)
  static adjustNoteToGlobal(note, genesisOctave: Octave = Octave.ZERO) {
    return this.getMiddleC() + note + this.getNoteOffsetForGenesisOctave(genesisOctave)
  }

  static getNoteOffsetForGenesisOctave(octave: Octave) {
    switch (octave) {
      case Octave.ZERO:
        return 0
      case Octave.MINUS_ONE:
        return -12
      case Octave.PLUS_ONE:
        return 12
    }
  }
  static getNoteFromGlobal(note) {
    return note - this.getMiddleC()
  }
  static getPianoRollTotalHeight() {
    return this.getMiddleC() * 2 + 1
  }
  static getMiddleC() {
    return 60
  }

  // If index is 0, return null
  static getPart(index, partA, partB, partC): InstrumentPart {
    if (index === 0) return null
    if (index === 1) return partA
    if (index === 2) return partB
    if (index === 3) return partC
    throw 'invalid part index: ' + index
  }

  // Maps genesis samples to their v2 counterparts.
  // If sample value is -1, it means it's no longer available in v2.
  static genesisSampleToV2(type: GenesisSampleType, sample: number): number {
    // See https://docs.google.com/spreadsheets/d/1WtU_sTdvI7HzmyC23tuNNKqqC10YjkX1CNTTwfs1lOg/edit#gid=0
    const bassMappings = [-1, -1, -1, -1, -1, -1, 9, 7, 8, -1, 23, 24, -1, 36, 25]
    const hatMappings = [-1, -1, -1, -1, -1, 11, -1, -1, 0, 1, 21, 22, 10]
    const kickMappings = [-1, -1, -1, -1, -1, -1, 13, -1, -1, 2, 28, 12]
    const melMappings = [
      -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, 3, 6, 4, -1, -1, -1, -1, 27, 26
    ]
    const snareMappings = [
      -1, -1, -1, -1, -1, -1, -1, 16, 17, -1, -1, 5, 20, -1, -1, -1, -1, 15, 18, 14
    ]
    const percMappings = [34, -1, -1, -1, -1, -1, 29, 30, 31, 35, 33, 37].concat(snareMappings)

    switch (type) {
      case GenesisSampleType.BASS:
        return bassMappings[sample]
      case GenesisSampleType.HAT:
        return hatMappings[sample]
      case GenesisSampleType.KICK:
        return kickMappings[sample]
      case GenesisSampleType.MELODY:
      case GenesisSampleType.HARMONY:
        return melMappings[sample]
      case GenesisSampleType.PERC:
        return percMappings[sample]
      case GenesisSampleType.SNARE:
        return snareMappings[sample]
    }
  }
}

export class Utils {
  static toHexString(byteArray: Uint8Array) {
    return Array.from(byteArray, function (byte) {
      return ('0' + (byte & 0xff).toString(16)).slice(-2)
    }).join('')
  }

  static fromHexString(hexString) {
    var substringPrefix = '0x'
    if (hexString.substring(0, 2) === substringPrefix) {
      hexString = hexString.substring(2, hexString.length)
    }

    return new Uint8Array(hexString.match(/.{1,2}/g).map((byte) => parseInt(byte, 16)))
  }
}
